<template>
  <div class="page" :class="{ 'is-chrome-hidden': chromeHidden }">
    <TheBackLink floating />
    <TheNav />
    <TheActions />

    <nav class="tabs" aria-label="Kind of work" :style="{ '--active': activeTabIndex }">
      <span class="tabs__glider" aria-hidden="true" />
      <button
        v-for="option in kinds"
        :key="option.id"
        type="button"
        class="tabs__pill"
        :class="{ 'is-active': tab === option.id }"
        :aria-pressed="tab === option.id"
        @click="tab = option.id"
      >
        {{ option.label }}
      </button>
    </nav>

    <!-- Columns rather than a grid. These run from a narrow portrait to a wide
         landscape, and a grid would either crop them to a common shape or leave
         a ragged gap under every short one. Columns let each keep its own
         proportions and pack against its neighbour. -->
    <!-- Shaped like the wall it is standing in for, in the same columns, so
         the layout does not move when the pictures land. -->
    <div v-if="pending" class="wall wall--pending" aria-hidden="true">
      <div v-for="n in 6" :key="`sk-${n}`" class="tile tile--skeleton">
        <span class="skeleton" :style="{ paddingTop: `${skeletonRatio(n)}%` }" />
      </div>
    </div>

    <!-- The same words, and the same way out, as the empty Videos tab on
         Explore. A reader who reaches the end of one and then the other should
         not be told two different things about the same absence. -->
    <div v-else-if="!items.length" class="empty">
      <div class="empty__inner">
        <h2 class="empty__title">{{ tab === 'videos' ? 'No videos here yet' : 'Nothing here yet' }}</h2>
        <p v-if="tab === 'videos'" class="empty__note">
          The moving work goes up on X and Instagram first. Have a look there
          while this fills up.
        </p>
        <p v-else class="empty__note">
          New pieces land here as they are made. There is more of the work on X
          and Instagram in the meantime.
        </p>

        <div class="empty__links">
          <a
            v-for="link in videoLinks"
            :key="link.name"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            class="empty__link"
          >
            <svg v-if="link.icon === 'x'" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
              <path d="M18.9 2H22l-7.1 8.1L23.2 22h-6.5l-5.1-6.7L5.8 22H2.7l7.6-8.7L1.2 2h6.6l4.6 6.1zm-1.1 18h1.7L7.3 3.8H5.5z" />
            </svg>
            <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <path d="M17.5 6.5h.01" />
            </svg>
            <span>{{ link.name }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Columns we fill, not columns the browser balances.
         `column-count` redistributes every item whenever the content changes,
         so each new page of pictures reshuffled the ones already on screen and
         the wall lurched under the reader. These are real elements: a picture
         is put in the shortest column when it arrives and stays where it was
         put, because appending never changes an earlier decision. -->
    <div v-else class="wall">
      <div v-for="(column, c) in columns" :key="`col-${c}`" class="wall__col">
        <!-- A render is not a link: the wall is for looking, and a picture that
             jumps you somewhere the moment you touch it is a menu pretending to
             be one. A story is the exception, because there is somewhere for it
             to go and the badge says so. -->
        <component
        :is="piece.story ? NuxtLink : 'div'"
          v-for="piece in column"
        :id="`piece-${piece.id}`"
        :key="piece.id"
        :to="piece.story ? `/library?sheet=${piece.id}` : undefined"
        class="tile"
        :class="{ 'is-called-out': calledOut === piece.id, 'is-story': piece.story }"
      >
        <!-- A clip lays out from its poster. The wall is for choosing one,
             not for playing seventeen. -->
        <!-- Per tile, not for the wall as a whole. A tile already reserves its
             own shape from the picture's dimensions, so nothing here moves; it
             was simply an empty box until the file arrived. And unlike a rail
             of four, waiting for every picture on a paginated wall before
             drawing any of them would be a stall rather than a courtesy. -->
        <span v-if="!shotLoaded[piece.id]" class="tile__sk skeleton" aria-hidden="true" />

        <img
          :src="stillFor(piece)"
          :srcset="srcsetFor(piece)"
          sizes="(max-width: 620px) 100vw, (max-width: 1100px) 50vw, 33vw"
          :alt="piece.title"
          :width="piece.width"
          :height="piece.height"
          loading="lazy"
          class="tile__image"
          @load="shotLoaded[piece.id] = true"
          @error="shotLoaded[piece.id] = true"
        >

        <!-- Says what it is before it is opened, because a portrait sitting
             among renders otherwise looks like another render. -->
        <span v-if="piece.story" class="tile__kind">Story</span>

        <span v-if="tab === 'videos'" class="tile__play" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <polygon points="8,5 19,12 8,19" />
          </svg>
        </span>

        <span class="tile__veil"/>
        <span class="tile__title">{{ piece.title }}</span>
        <span class="tile__right">
          <CountBadge v-if="viewsFor(piece.id)" :count="viewsFor(piece.id)" kind="view" class="tile__views" />

          <ShareButton
            v-if="!piece.story"
            :id="piece.id"
            :kind="kindKey"
            :title="piece.title"
          />
        </span>
        </component>
      </div>
    </div>

    <!-- Comes into view before the wall runs out, so the next batch is usually
         already there by the time the reader reaches the bottom. The button is
         not a fallback for that: it is what makes this reachable from a
         keyboard, and what gives somebody a way to stop scrolling and choose. -->
    <div v-if="!pending && hasMore" ref="sentinel" class="more">
      <button type="button" class="more__button" @click="showMore">
        Show more
        <span class="more__count">{{ visible.length }} of {{ items.length }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { reactive, resolveComponent } from 'vue';
import { aiClips, aiGenerations, personalInfo } from '~/data/content';

// Resolved once, rather than named as a string in the template. `:is` with a
// string only works if Vue can look the name up; when it cannot it renders a
// literal <nuxtlink> element instead, which looks exactly like the tile it was
// meant to be and does nothing at all when clicked.
const NuxtLink = resolveComponent('NuxtLink');

const thumb = (src) => (src || '').replace('/images/ai/', '/images/ai/thumb/');
const mid = (src) => (src || '').replace('/images/ai/', '/images/ai/mid/');

const kinds = [
  { id: 'images', label: 'Images' },
  { id: 'videos', label: 'Videos' },
];

const tab = ref('images');
const activeTabIndex = computed(() => kinds.findIndex((k) => k.id === tab.value));
// Stories sit among the renders rather than in a row of their own. The wall is
// what there is to look at, and a portrait with a story behind it is not a
// different kind of thing to look at.
const items = computed(() =>
  tab.value === 'images' ? [...pieces.value, ...stories.value] : aiClips
);

// How many columns the wall is running. Read rather than assumed, because the
// distribution below has to agree with what the stylesheet is doing.
//
// Safe to read the viewport here: `pending` is true while prerendering, so the
// wall itself is only ever built on the client and there is no server render to
// disagree with.
const columnCount = ref(3);

function measureColumns() {
  if (typeof window === 'undefined') return;

  columnCount.value = window.matchMedia('(max-width: 620px)').matches
    ? 1
    : window.matchMedia('(max-width: 1100px)').matches
      ? 2
      : 3;
}

// Each picture goes to whichever column is shortest when it arrives.
//
// Shortest by predicted height, not measured: every piece carries its own
// dimensions, so the height a tile will take is its aspect ratio times the
// column width, and the width is the same for all of them. That means no
// measuring, no waiting for images, and the same answer on every pass.
//
// Recomputed from the whole visible list rather than appended to, which sounds
// wasteful and is the point: the pass is greedy and left to right, so the first
// twelve placements are identical whatever follows them. Adding a page cannot
// move a picture that was already on screen.
const columns = computed(() => {
  const buckets = Array.from({ length: columnCount.value }, () => []);
  const heights = new Array(columnCount.value).fill(0);

  for (const piece of visible.value) {
    let shortest = 0;
    for (let i = 1; i < heights.length; i += 1) {
      if (heights[i] < heights[shortest]) shortest = i;
    }

    buckets[shortest].push(piece);

    // In units of column width. A piece with no dimensions is treated as a
    // square, which is wrong but stable, and only happens for a sheet saved
    // before portraits carried their shape.
    const w = Number(piece.width) || 1;
    const h = Number(piece.height) || 1;
    heights[shortest] += h / w;
  }

  return buckets;
});

// The dock, the tabs, the back link and the actions cluster all float over the
// wall, and on a phone they cover most of the top of it. They earn that while
// the reader is deciding where to go and not while they are looking, so they
// step aside on the way down and come back on the way up.
const chromeHidden = ref(false);
let lastY = 0;

function onChromeScroll() {
  // Desktop has the room, and a reader who narrows the window should get the
  // chrome back rather than keep whatever the last swipe decided.
  if (!window.matchMedia('(max-width: 768px)').matches) {
    chromeHidden.value = false;
    return;
  }

  const y = window.scrollY;

  // Nothing to gain by hiding at the top, and the threshold stops a
  // rubber-band bounce at either end from flipping it.
  if (y < 96) chromeHidden.value = false;
  else if (Math.abs(y - lastY) > 8) chromeHidden.value = y > lastY;

  lastY = y;
}

onMounted(() => {
  measureColumns();
  window.addEventListener('resize', measureColumns);

  lastY = window.scrollY;
  window.addEventListener('scroll', onChromeScroll, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', measureColumns);
  window.removeEventListener('scroll', onChromeScroll);
});

// A wall this long has no business decoding every picture to show the first
// screenful. Twelve is roughly four rows at three columns, so the next batch is
// always being fetched well before the reader could reach the end of this one.
const PAGE = 12;
const shown = ref(PAGE);

// How far the wall had been opened, kept for a reload.
//
// The offset alone is only half a position on a list that pages. This started
// at twelve on every load, so a reader who had opened forty tiles and scrolled
// down came back to twelve and a saved offset pointing past the end of them:
// there was nothing at that height to land on. Per tab, because the two walls
// are paged separately and a video count means nothing to the pictures.
const SHOWN_KEY = 'gallery-shown';
const shownKey = () => `${SHOWN_KEY}:${tab.value}`;

function rememberShown() {
  try {
    sessionStorage.setItem(shownKey(), String(shown.value));
  } catch {
    // private mode / storage disabled
  }
}

function restoreShown() {
  try {
    const saved = Number(sessionStorage.getItem(shownKey()));
    if (Number.isFinite(saved) && saved > shown.value) shown.value = saved;
  } catch {
    // private mode / storage disabled
  }
}

watch(shown, rememberShown);

const visible = computed(() => items.value.slice(0, shown.value));
const hasMore = computed(() => shown.value < items.value.length);

function showMore() {
  shown.value = Math.min(shown.value + PAGE, items.value.length);
}

// A different tab is a different wall, and starting it halfway down would be
// nobody's idea of where to begin.
watch(tab, () => {
  shown.value = PAGE;
  // Deliberate, so the stored count has to follow rather than put the reader
  // back where the other tab left them.
  rememberShown();
  restoreShown();
});

// Auto-advance. The button underneath does the same thing for a keyboard, and
// for anyone who would rather decide than be fed.
const sentinel = ref(null);
let watcher = null;

function watchSentinel() {
  watcher?.disconnect();
  if (!sentinel.value || typeof IntersectionObserver === 'undefined') return;

  watcher = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) showMore();
    },
    // Well before it is on screen, so the batch lands during the scroll rather
    // than after it stops.
    { rootMargin: '800px 0px' }
  );

  watcher.observe(sentinel.value);
}

// The element is created and destroyed as the wall runs out and grows again,
// so the observer follows it rather than being attached once.
watch(sentinel, watchSentinel);
onBeforeUnmount(() => watcher?.disconnect());

// Pictures and clips are counted under different kinds, and a link back to
// Explore has to name the tab for a clip or it lands on the images.
const kindKey = computed(() => (tab.value === 'images' ? 'piece' : 'clip'));
// Which tiles have their picture. Keyed by id rather than index, because a
// tile changes position when the live rows land and must not inherit the answer
// of whatever was in that slot before.
const shotLoaded = reactive({});

// The two accounts the moving work goes up on, which is what the empty tab
// sends people to. The same pair Explore uses.
const videoLinks = computed(() =>
  personalInfo.socialLinks.filter((link) => link.icon === 'x' || link.icon === 'instagram')
);

const isBuiltIn = (src) => (src || '').startsWith('/images/ai/');
const stillFor = (item) =>
  tab.value === 'videos' ? item.poster : (isBuiltIn(item.image) ? thumb(item.image) : item.image);

// Only the pictures that ship with the site have thumb and mid variants to
// choose between. An uploaded one is a single file, and naming it twice at two
// widths would tell the browser something untrue.
const srcsetFor = (item) =>
  tab.value === 'images' && isBuiltIn(item.image)
    ? `${thumb(item.image)} 640w, ${mid(item.image)} 1000w`
    : undefined;

// Counts are shown here but never recorded here. A picture appearing in a wall
// of seventeen is not somebody looking at it, and counting that would bury the
// difference between a piece people stop on and one they scrolled past on the
// way somewhere else. Opening one goes to Explore, which centres it, and
// that is where it counts.
const { load, useCounts, usePending } = useLiveContent();
const counts = useCounts();
const pending = usePending();

// Live rows lead, then whatever is compiled in that the database has not
// replaced. Nothing changes until something is uploaded, and when there is
// enough on the server the built-in list can simply be emptied.
const pieces = ref(aiGenerations);
const stories = ref([]);

onMounted(async () => {
  // Before the rows arrive, so the tiles are in place as soon as they do and
  // the height the restore is aiming at is real.
  restoreShown();

  // A shared clip link opens on the right tab before anything is looked up.
  if (route.query.tab === 'videos') tab.value = 'videos';

  const [live, sheets] = await Promise.all([
    load('generations', aiGenerations),
    load('sheets', []),
  ]);

  pieces.value = live;

  // Only the ones marked for it. They follow the library's lock without being
  // told to: when it is closed the server withholds the sheets entirely, so
  // there is nothing here to put on the wall, and no tile leading to a door
  // the site has decided to shut.
  stories.value = sheets
    .filter((sheet) => sheet.featured && sheet.image)
    .map((sheet) => ({
      id: sheet.id,
      title: sheet.name,
      image: sheet.image,
      width: sheet.width,
      height: sheet.height,
      story: true,
    }));

  await openShared();
});

// Three shapes in rotation rather than one. A column of identical boxes reads
// as a table waiting to load; a wall reads as pictures waiting to load.
const SKELETON_RATIOS = [140, 75, 110];
const skeletonRatio = (n) => SKELETON_RATIOS[(n - 1) % SKELETON_RATIOS.length];

function viewsFor(id) {
  return Number(counts.value?.[kindKey.value]?.[id]) || 0;
}

// Sharing from the wall means "look at this room", so the link comes back here
// rather than opening the piece on its own. Landing on it, the tile is brought
// into view and briefly called out, because a wall of seventeen otherwise
// answers a shared link by looking identical to the front page of it.
const route = useRoute();
const calledOut = ref(null);
let calloutTimer = null;


// Run once the rows are in, not on mount.
//
// An uploaded picture is not in the served HTML, so the blocking script cannot
// find it and neither can anything that reads the list before the fetch lands.
// Waiting costs nothing here: the script has already handled the built-in case
// before the first paint, and this is the only path that works for the rest.
async function openShared() {
  const wanted = route.query.piece || route.query.clip;
  if (!wanted) return;

  // Paging means the tile may not be rendered even now. There is nothing to
  // scroll to until the wall has been opened far enough to hold it.
  const at = items.value.findIndex((piece) => piece.id === wanted);
  if (at < 0) return;
  if (at >= shown.value) shown.value = Math.min(at + PAGE, items.value.length);

  await nextTick();

  const el = document.getElementById(`piece-${wanted}`);
  if (!el) return;

  // Instant rather than smooth. If the blocking script already put the page
  // here this is a no-op; if it could not, a jump is honest where an animation
  // would be the drift it exists to avoid.
  el.scrollIntoView({ block: 'center' });
  calledOut.value = wanted;
  calloutTimer = setTimeout(() => {
    calledOut.value = null;
  }, 2600);

  // The query has done its job, and a bookmarked link should not keep calling
  // the same tile out for ever.
  window.history.replaceState(window.history.state, '', window.location.pathname);
}

onBeforeUnmount(() => {
  clearTimeout(calloutTimer);
  // Forgotten on the way out, not kept: it is meant to survive a reload, and
  // leaving the page is the only thing that runs this. The same reasoning as
  // the offset it belongs to.
  try {
    sessionStorage.removeItem(`${SHOWN_KEY}:images`);
    sessionStorage.removeItem(`${SHOWN_KEY}:videos`);
  } catch {
    // private mode / storage disabled
  }
});

// The wall is long, so a reload has somewhere to fall from. Same treatment as
// the other ordinary pages: the position is saved as it scrolls and the
// blocking script in nuxt.config puts it back before the first paint, rather
// than letting the browser restore against a document that is not tall enough
// yet and clamp to the top.
usePageScroll();

useHead({
  title: `Gallery - ${personalInfo.fullName}`,
  meta: [
    {
      name: 'description',
      content: `Every generated piece by ${personalInfo.fullName}.`,
    },
    { property: 'og:title', content: `Gallery - ${personalInfo.fullName}` },
    { property: 'og:description', content: `Every generated piece by ${personalInfo.fullName}.` },
    { name: 'twitter:title', content: `Gallery - ${personalInfo.fullName}` },
    { name: 'twitter:description', content: `Every generated piece by ${personalInfo.fullName}.` },
  ],
});
</script>

<style lang="scss" scoped>
.page {
  min-height: 100vh;
  min-height: 100svh;
  padding: clamp(4.5rem, 10vh, 6.5rem) clamp(1rem, 4vw, 3rem) clamp(3rem, 8vh, 5rem);
  background: #0b0f14;
  color: #fff;
}

// The navigation dock is fixed down the left and its right edge sits at 68px,
// which the wall ran under at every width below about 1600: measured 10px of
// overlap at 1440, 17px at 1280, 26px at 900. Elsewhere on the site the dock
// floats over content that is centred and has room to spare, but a wall of
// pictures fills its container by definition, so the first column has to be
// told to start after it.
//
// Both sides, not just the left. Clearing only the side the dock is on leaves
// the wall visibly off-centre, which reads as a mistake rather than as room
// made for something.
@media (min-width: 769px) {
  .page {
    padding-inline: 5.5rem;
  }
}

// Nearly the whole screen for the pictures, with just enough margin to keep
// them off the edges.
//
// No clearance for the dock here, unlike everywhere else. A phone has room for
// one column of pictures or a column of chrome beside a narrower one, and the
// wall is what the page is for; the dock floats over it and steps out of the
// way as soon as the reader moves.
@media (max-width: 768px) {
  .page {
    padding-left: 0.6rem;
    padding-right: 0.6rem;
  }

  .wall {
    max-width: 100%;
  }
}

// Everything fixed over the wall goes together, so nothing is left hanging on
// its own once the reader is moving.
@media (max-width: 768px) {
  .tabs,
  :deep(.side-nav),
  :deep(.header-actions),
  :deep(.back-link) {
    transition: opacity 0.25s ease-in-out, transform 0.25s ease-in-out;
  }

  .page.is-chrome-hidden {
    .tabs,
    :deep(.side-nav),
    :deep(.header-actions),
    :deep(.back-link) {
      opacity: 0;
      pointer-events: none;
    }
  }
}







.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: min(1400px, 92vw);
  // Enough of the screen to be the page rather than a line under a heading,
  // without claiming a whole one on a phone where the tabs are just above it.
  min-height: 50vh;
  margin: 2rem auto 0;
  padding: clamp(1.5rem, 5vw, 3rem) 0;
}

.empty__inner {
  max-width: 34rem;
  text-align: center;
}

.empty__title {
  margin: 0 0 0.6rem;
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(1.15rem, 2.6vw, 1.6rem);
  color: #fff;
}

.empty__note {
  margin: 0 0 1.6rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.62);
}

.empty__links {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.7rem;
}

.empty__link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.15rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  text-decoration: none;
  font-size: 0.85rem;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    text-decoration: none;
    border-color: rgba(var(--accent-rgb), 0.7);
    background: rgba(var(--accent-rgb), 0.14);
  }
}

// Says it moves, without playing anything.
.tile__kind {
  position: absolute;
  top: 0.6rem;
  left: 0.7rem;
  padding: 0.16rem 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(11, 15, 20, 0.6);
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
}

// A story opens and a render does not. The badge already says which is which,
// so the cursor is the only other difference worth drawing.
.tile.is-story {
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.tile__play {
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  transform: translate(-50%, -50%);
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(11, 15, 20, 0.45);
  color: #fff;
  pointer-events: none;
}

// The same control the featured page carries, in the same place, because they
// are the same choice made in two rooms.
.tabs {
  position: fixed;
  z-index: 1900;
  top: clamp(1.15rem, 3vh, 1.9rem);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.3rem;
  padding: 0.25rem;
  border-radius: 999px;
  background: rgba(10, 14, 20, 0.86);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.tabs__glider {
  position: absolute;
  top: 0.25rem;
  bottom: 0.25rem;
  left: 0.25rem;
  width: calc((100% - 0.5rem - 0.3rem) / 2);
  border-radius: 999px;
  background: var(--accent);
  transform: translateX(calc(var(--active, 0) * (100% + 0.3rem)));
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}

.tabs__pill {
  appearance: none;
  border: none;
  cursor: pointer;
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  white-space: nowrap;
  padding: 0.42rem 1.05rem;
  border-radius: 999px;
  background: transparent;
  color: rgba(255, 255, 255, 0.66);
  font-family: 'Orbitron', sans-serif;
  font-size: 0.68rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  transition: color 0.2s ease;

  &:hover,
  &.is-active {
    color: #fff;
  }

  @media (max-width: 768px) {
    padding: 0.38rem 0.85rem;
    font-size: 0.62rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tabs__glider {
    transition: none;
  }
}

.wall {
  max-width: min(1400px, 92vw);
  margin: 0 auto;
  display: flex;
  // Each column is as tall as its own contents; nothing stretches to match its
  // neighbour.
  align-items: flex-start;
  gap: clamp(0.6rem, 1.4vw, 1.1rem);
}

// The placeholder takes its columns from CSS, not from the measurement.
//
// The wall itself is built in script, because placing each picture in the
// shortest column means knowing the heights. The placeholder standing in for it
// needs none of that, and reading the count from a ref meant three columns on a
// phone until the first measurement landed after mount: the wrong shape at
// exactly the moment it is the only thing on the screen.
//
// The breakpoints are the ones measureColumns uses, so the placeholder and the
// wall it stands in for never disagree.
.wall--pending {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.wall__col {
  // Equal widths whatever is in them, which is what lets the height of a tile
  // be predicted from its aspect ratio alone.
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(0.6rem, 1.4vw, 1.1rem);
}

.tile {
  position: relative;
  display: block;
  border-radius: 10px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  // Its own layer, so the hover does not repaint the column beside it.
  isolation: isolate;

  &:hover,
  &:focus-within {
    .tile__image { transform: scale(1.04); }

    .tile__veil,
    .tile__title,
    .tile__right { opacity: 1; }
  }
}


.more {
  display: flex;
  justify-content: center;
  padding: clamp(1.5rem, 5vh, 3rem) 0 0;
}

.more__button {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 1.2rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.04);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: border-color 0.2s ease-in-out, color 0.2s ease-in-out;

  &:hover,
  &:focus-visible {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.45);
  }
}

.more__count {
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.5);
}

.tile--skeleton {
  // The ratio is carried by padding so the box has a height before anything is
  // in it, which is the whole job here.
  span {
    display: block;
    width: 100%;
    height: 0;
  }
}

.tile__sk {
  position: absolute;
  inset: 0;
  // Over the tile rather than behind it, so it stops existing once the picture
  // is there instead of animating under every loaded image for the life of the
  // page.
  z-index: 1;
  border-radius: inherit;
}

.tile__image {
  display: block;
  width: 100%;
  height: auto;
  transition: transform 0.45s ease;
}

.tile__veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(11, 15, 20, 0.85) 0%, rgba(11, 15, 20, 0) 55%);
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
}

.tile__title {
  position: absolute;
  left: 0;
  bottom: 0;
  max-width: 62%;
  padding: 0.7rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}


.tile__right {
  position: absolute;
  right: 0;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.tile__views {
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.7);
}


// Arrived here from a shared link. Loud enough to find in a wall, brief enough
// not to become part of the design.
.tile.is-called-out {
  outline: 2px solid rgba(255, 255, 255, 0.85);
  outline-offset: 3px;

  .tile__veil,
  .tile__title,
  .tile__right {
    opacity: 1;
  }
}

// A pointer is what reveals the caption. Without one there is nothing to
// reveal it with, so it simply stays visible.
@media (hover: none) {
  .tile__veil,
  .tile__title,
  .tile__right {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tile__image,
  .tile__veil,
  .tile__title,
  .tile__right {
    transition: none;
  }
}
</style>
